import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Item } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  pickServiceGroupName,
  Subscription,
} from '@/types/subscriptions/subscription';
import { pages, formatRoute } from '@/settings/pages';
import { weekDayDateFormat, startDateFormat } from '@/utils/date';

import { ServiceName } from './ServiceName';

type Props = {
  children?: never;
  subscriptions: Subscription[];
};

export const StyledItemGroup: React.FC<Props> = (props) => {
  const { subscriptions } = props;
  const { t } = useTranslation();
  return (
    <Wrapper divided={true}>
      {subscriptions.map((subscription) => (
        <Link
          data-test={`subscription-list-item-${subscription.requestCode}`}
          key={subscription.id}
          to={formatRoute(pages.subscription.detail, {
            requestCode: subscription.requestCode,
          })}
        >
          <Item>
            <Item.Content className="subscription-list-item">
              <Grid>
                <Grid.Column width={8}>
                  <Item.Header className="subscription-list-item-header">
                    <span className="subscription-list-item-header-name">
                      {pickServiceGroupName(subscription)}
                    </span>
                    {subscription.isUpdatable && (
                      <span className="subscription-list-item-header-label">
                        {t('ui.expression.changeable')}
                      </span>
                    )}
                  </Item.Header>
                  <Item.Extra className="subscription-list-item-services">
                    <ServiceName subscription={subscription} />
                  </Item.Extra>
                </Grid.Column>
                <Grid.Column
                  width={8}
                  className="subscription-list-item-detail"
                >
                  <div className="subscription-list-item-detail-heading">
                    <div>
                      <span className="subscription-list-item-detail-label">
                        {`${t('models.subscription.requestCode')}： `}
                      </span>
                      {subscription.requestCode}
                    </div>
                  </div>
                  <Grid>
                    <Grid.Row columns={2}>
                      <Grid.Column>
                        <span className="subscription-list-item-detail-label">
                          {`${t('models.subscription.appliedAt')}： `}
                        </span>
                        {subscription.appliedAt &&
                          weekDayDateFormat({ date: subscription.appliedAt })}
                      </Grid.Column>
                      <Grid.Column>
                        <span className="subscription-list-item-detail-label">
                          {`${t('models.subscription.startsOn')}： `}
                        </span>
                        {subscription.startsOn &&
                          startDateFormat({ date: subscription.startsOn })}
                      </Grid.Column>
                      <Grid.Column>
                        <span className="subscription-list-item-detail-label">
                          {`${t('models.subscription.users')}： `}
                        </span>
                        {`${subscription.userCount}${t(
                          'ui.unit.studentNumber',
                        )}`}
                      </Grid.Column>
                      {subscription.projectClass && (
                        <Grid.Column>
                          <span className="subscription-list-item-detail-label">
                            {`${t('models.subscription.projectClass')}： `}
                          </span>
                          {subscription.projectClass.name}
                        </Grid.Column>
                      )}
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid>
            </Item.Content>
          </Item>
        </Link>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled(Item.Group)`
  a {
    display: block;
    padding: 2.2em 2.6em;
    border: 1px solid rgba(34, 36, 38, 0.15);
    margin-bottom: -1px;
    background-color: #fff;

    &:hover {
      opacity: 0.75;
    }
  }

  .subscription-list-item a:hover {
    background: rgba(250, 250, 255, 0.75);
    opacity: 0.75;
  }

  .subscription-list-item-services {
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.2em;
  }

  .subscription-list-item-services > div,
  .subscription-list-item-services > span {
    display: inline;
    margin: 0 0 0.2em;
    font-size: 0.75em;
  }

  .subscription-list-item-services > span {
    margin-right: 0.5em;
  }

  .subscription-list-item-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 0.5em;
    color: rgba(0, 0, 0, 0.87) !important;
    font-size: 1em;
    font-weight: bold;
  }

  .subscription-list-item-header-name {
    margin-right: 0.33em !important;
  }

  .subscription-list-item-header-label {
    padding: 0.33em 0.45em !important;
    color: #ff7701;
    font-size: 0.65em;
  }

  .header.subscription-list-item-detail-heading {
    margin-bottom: 0.33em !important;
    font-weight: 400 !important;
  }

  .subscription-list-item-detail {
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.75em;
  }

  .subscription-list-item-detail-label {
    color: rgba(0, 0, 0, 0.4);
  }

  .subscription-list-item-detail-heading {
    padding-bottom: 0.6em;
  }

  .subscription-list-item-detail .row {
    padding-bottom: 0 !important;
  }

  .subscription-list-item-detail .column {
    padding-bottom: 0.5em;
  }
`;
